import { useState } from "react";
import axios from "axios";
import { X, FileDown, Loader2, CheckCircle2 } from "lucide-react";

// Public endpoint — call without the authenticated axios instance so we don't
// require a CSRF cookie. The backend exempts this path from CSRF.
const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SamplePackModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", org: "", role: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await axios.post(`${PUBLIC_API}/public/sample-pack`, {
        name: form.name.trim(),
        email: form.email.trim(),
        org: form.org.trim() || undefined,
        role: form.role.trim() || undefined,
      });
      setSent(true);
      setTimeout(() => { onClose(); setSent(false); setForm({ name: "", email: "", org: "", role: "" }); }, 2800);
    } catch (e2) {
      const d = e2?.response?.data;
      const msg = (d && typeof d === "object") ? d.detail : null;
      setErr(msg || "Could not send sample. Try again in a minute.");
    } finally {
      setBusy(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
         data-testid="sample-pack-modal" onClick={onClose}>
      <div className="w-full max-w-md flat-card p-7 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" data-testid="sample-modal-close"
                className="absolute top-3 right-3 text-[var(--text-2)] hover:text-white">
          <X size={18} />
        </button>

        {!sent ? (
          <>
            <div className="flex items-center gap-2 mb-2">
              <FileDown size={20} color="var(--cyan)" strokeWidth={1.5} />
              <div className="overline">// SAMPLE_PACK</div>
            </div>
            <h2 className="font-display font-bold text-2xl uppercase tracking-tight">What procurement sees</h2>
            <p className="mt-2 text-sm text-[var(--text-2)] leading-relaxed">
              We'll email you a signed sample compliance pack PDF — the exact artefact your tender-evidence binder will contain once your squad is on DefDossier.
            </p>

            <form onSubmit={submit} className="mt-5 space-y-3">
              <div>
                <label className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">Name</label>
                <input required value={form.name} onChange={upd("name")} className="input-tac mt-1"
                       placeholder="Joel Smith" data-testid="sample-name" />
              </div>
              <div>
                <label className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">Work email</label>
                <input required type="email" value={form.email} onChange={upd("email")} className="input-tac mt-1"
                       placeholder="joel@your-contractor.com" data-testid="sample-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">Org (opt.)</label>
                  <input value={form.org} onChange={upd("org")} className="input-tac mt-1"
                         placeholder="ACME Defence" data-testid="sample-org" />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">Role (opt.)</label>
                  <input value={form.role} onChange={upd("role")} className="input-tac mt-1"
                         placeholder="L&D Manager" data-testid="sample-role" />
                </div>
              </div>

              {err && <div className="font-mono text-xs text-[var(--red)] border border-[var(--red)] p-2"
                            data-testid="sample-error">{err}</div>}

              <button type="submit" disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2"
                      data-testid="sample-submit">
                {busy ? <><Loader2 size={14} className="animate-spin" /> Transmitting…</> : "Send Me The Sample Pack"}
              </button>
              <p className="font-mono text-[10px] text-[var(--text-2)] text-center mt-2">
                // arrives in ~30s · no spam · one email per request
              </p>
            </form>
          </>
        ) : (
          <div className="py-6 text-center" data-testid="sample-sent">
            <CheckCircle2 size={42} color="var(--green)" strokeWidth={1.5} className="mx-auto" />
            <h2 className="font-display font-bold text-2xl uppercase mt-4">Transmission sent</h2>
            <p className="mt-2 text-sm text-[var(--text-2)]">Check your inbox at <span className="text-white">{form.email}</span> in about 30 seconds.</p>
          </div>
        )}
      </div>
    </div>
  );
}
